import { useQuery } from '@tanstack/react-query';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CardSkeleton } from '../components/LoadingSkeleton';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { api } from '../lib/api';

function formatDollars(cents: number) {
  if (cents >= 100_000_00) {
    return `$${(cents / 100_000_00).toFixed(1)}M`;
  }
  if (cents >= 1_000_00) {
    return `$${(cents / 1_000_00).toFixed(1)}K`;
  }
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

function formatPercent(rate: number) {
  return `${Math.round(rate * 100)}%`;
}

interface KpiCardProps {
  title: string;
  value: string;
  subtitle?: string;
}

function KpiCard({ title, value, subtitle }: KpiCardProps) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{title}</p>
      <p className="mt-2 text-3xl font-bold text-gray-900">{value}</p>
      {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
    </div>
  );
}

export function Dashboard() {
  const metricsQuery = useQuery({
    queryKey: ['dashboard-metrics'],
    queryFn: () => api.dashboard.metrics(),
  });

  const trendsQuery = useQuery({
    queryKey: ['dashboard-trends'],
    queryFn: () => api.dashboard.trends(),
  });

  const payerQuery = useQuery({
    queryKey: ['dashboard-payers'],
    queryFn: () => api.dashboard.byPayer(),
  });

  const metrics = metricsQuery.data;
  const trends = trendsQuery.data ?? [];
  const topPayers = (payerQuery.data ?? []).slice(0, 5);

  return (
    <ErrorBoundary>
      <div className="space-y-6">
        <h1 className="text-xl font-semibold text-gray-900">Dashboard</h1>

        {/* KPI cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {metricsQuery.isLoading ? (
            <>
              <CardSkeleton />
              <CardSkeleton />
              <CardSkeleton />
              <CardSkeleton />
            </>
          ) : metrics ? (
            <>
              <KpiCard
                title="Appeals Submitted"
                value={metrics.appeals_submitted.toLocaleString()}
              />
              <KpiCard
                title="Win Rate"
                value={formatPercent(metrics.win_rate)}
                subtitle="of submitted appeals"
              />
              <KpiCard
                title="Dollars Recovered"
                value={formatDollars(metrics.dollars_recovered_cents)}
              />
              <KpiCard
                title="Avg Days to Resolution"
                value={
                  metrics.avg_days_to_resolution != null
                    ? `${metrics.avg_days_to_resolution}d`
                    : '—'
                }
                subtitle="submission to outcome"
              />
            </>
          ) : null}
        </div>

        {/* Recovery trend chart */}
        <div className="rounded-lg border border-gray-200 bg-white p-5">
          <h2 className="mb-4 text-sm font-semibold text-gray-700">Recovery Trend</h2>
          {trendsQuery.isLoading ? (
            <div className="h-48 animate-pulse rounded bg-gray-100" />
          ) : trends.length === 0 ? (
            <div className="flex h-48 items-center justify-center text-sm text-gray-400">
              No trend data yet.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={trends} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v: string) =>
                    new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  }
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v: number) => `$${(v / 100).toLocaleString()}`}
                />
                <Tooltip
                  formatter={(value: number) => [
                    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                      value / 100,
                    ),
                    'Recovered',
                  ]}
                  labelFormatter={(label: string) =>
                    new Date(label).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                    })
                  }
                />
                <Line
                  type="monotone"
                  dataKey="recovered_cents"
                  stroke="#cc0000"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Bottom tables */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {/* Top payers by denial volume */}
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <h2 className="mb-4 text-sm font-semibold text-gray-700">
              Top 5 Payers by Denial Volume
            </h2>
            {payerQuery.isLoading ? (
              <div className="animate-pulse space-y-2">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-4 rounded bg-gray-100" />
                ))}
              </div>
            ) : topPayers.length === 0 ? (
              <p className="text-sm text-gray-400">No data yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500">
                    <th className="pb-2 font-medium">Payer</th>
                    <th className="pb-2 text-right font-medium">Denials</th>
                    <th className="pb-2 text-right font-medium">Denied $</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {topPayers.map((row) => (
                    <tr key={row.payer_id}>
                      <td className="py-2 text-gray-900">{row.payer_name}</td>
                      <td className="py-2 text-right text-gray-600">
                        {row.denial_count.toLocaleString()}
                      </td>
                      <td className="py-2 text-right text-gray-600">
                        {formatDollars(row.total_denied_cents)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Top CARC codes — shown in same query as payers for now */}
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <h2 className="mb-4 text-sm font-semibold text-gray-700">
              Top 5 CARC Codes by Frequency
            </h2>
            <p className="text-sm text-gray-400">
              Data available once denials are imported.
            </p>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
}
